import type { FeatureFlagProvider } from './provider';

interface FeatureFlagRegistry {
  provider: FeatureFlagProvider | undefined;
  subscribers: Set<() => void>;
}

// The registry is a process-wide singleton anchored to `window`, mirroring the icon
// provider registry. @abax/platform is NOT shared as a Module Federation singleton, so
// the shell and each MFE evaluate their own copy of this module — a module-local
// registry would leave the provider the shell registered invisible to MFE consumers.
// The window anchor gives every copy one registry. Falls back to a module-local
// registry where `window` is absent (SSR, Node test env).
const REGISTRY_KEY = '__ABAX_FEATURE_FLAG_PROVIDER__';
let fallbackRegistry: FeatureFlagRegistry | undefined;

function createRegistry(): FeatureFlagRegistry {
  return { provider: undefined, subscribers: new Set() };
}

function getRegistry(): FeatureFlagRegistry {
  if (typeof window === 'undefined') {
    fallbackRegistry ??= createRegistry();
    return fallbackRegistry;
  }
  const w = window as unknown as Record<string, FeatureFlagRegistry | undefined>;
  w[REGISTRY_KEY] ??= createRegistry();
  return w[REGISTRY_KEY] as FeatureFlagRegistry;
}

/**
 * Register the process-wide feature-flag provider. Single-provider: re-registering
 * replaces the previous provider. Notifies every {@link onFeatureFlagProviderChange}
 * subscriber so late registration (a shell that wires its provider after the DevTools
 * panel mounts, or dev-shell's async catalog producer) re-populates the catalog.
 */
export function registerFeatureFlagProvider(provider: FeatureFlagProvider): void {
  const registry = getRegistry();
  registry.provider = provider;
  for (const cb of registry.subscribers) {
    try {
      cb();
    } catch {
      // A faulty subscriber must not break provider registration for the rest.
    }
  }
}

/** Read the registered provider, or `undefined` if none has been registered. */
export function getFeatureFlagProvider(): FeatureFlagProvider | undefined {
  return getRegistry().provider;
}

/**
 * Subscribe to provider (re-)registration. Fires whenever a provider is registered or
 * replaced, so a consumer that read an absent/earlier provider can re-resolve. Returns
 * an unsubscribe function. Window-anchored like the registry, so a provider registered
 * by the host is observable from any @abax/platform copy.
 */
export function onFeatureFlagProviderChange(cb: () => void): () => void {
  const { subscribers } = getRegistry();
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/** @internal Test/HMR helper — clears the registered provider and subscribers. */
export function __resetFeatureFlagRegistry(): void {
  const registry = getRegistry();
  registry.provider = undefined;
  registry.subscribers.clear();
}
